import { Form, Option, useFormApi, useFormState } from "informed";
import * as React from "react";
import * as Immutable from "immutable";
import { Dropdown } from "../../forms/informedv2/Dropdown";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { DataCorrectionRequestFormRecord } from "./DataCorrectionRequestFormRecord";
import {
    AddRecordAction,
    RemoveRecordAction,
    SelectSiteOptionAction,
    SelectSubjectOptionAction,
    SelectVisitOptionAction,
    SelectSessionOptionAction,
    SetVisibleWizardAction,
    AddMiscCorrectionAction,
    AddCorrectionAction,
    ResetFormAction,
    DcfRequestActionType,
    DcfRequestAction,
    LoadingSubjectOptionsAction,
    LoadSubjectsOptionsAction,
    LoadVisitOptionsAction,
    LoadSessionOptionsAction,
    FailedToSaveAction,
    SaveSuccessAction,
    SubmittingAction,
    ResetErrorAction,
    SetCorrectionTypeAction

} from "./DcfRequestActions";
import { DcfRecordState, Correction, MiscCorrection, VisibleWizards, DcfRequestState, initialDcfRequestState, defaultRecordId } from "./DcfRequestReducer";
import { TextBox } from "../../forms/informedv2/TextBox";
import { SiteOptionsSelector } from "./Selectors/SiteOptions";
import DocumentTitle from "../../misc/components/DocumentTitle";
import { MiscCorrectionWizard } from "../Execution/CorrectionTypes/Misc/MiscCorrectionWizard";
import { SubjectTypeCorrectionWizard } from "../Execution/CorrectionTypes/SubjectType/SubjectTypeCorrectionWizard";
import { RecordingStartCorrectionWizard } from "../Execution/CorrectionTypes/RecordingStart/RecordingStartCorrectionWizard";
import { SubjectIdCorrectionWizard } from "../Execution/CorrectionTypes/SubjectId/SubjectIdCorrectionWizard";
import { SuppressCoughSessionWizard } from "../Execution/CorrectionTypes/SuppressCoughSession/SuppressCoughSessionWizard";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { Link } from "react-router-dom";
import { MediaSerialCorrectionWizard } from '../Execution/CorrectionTypes/MediaSerial/MediaSerialCorrectionWizard';
import { EntityLabelCorrectionWizard } from '../Execution/CorrectionTypes/EntityLabels/EntityLabelCorrectionWizard';
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../../Core/SecurityContext";
import { DcfRequest, WebPortalClient, Site, Subject, QueryOfVisit, QueryOfSession, DcfCorrection } from "webportalclient";
import { AttachmentRelatesTo } from "../../file-attachments/models";
import { SwapLabelsWizard } from "../Execution/CorrectionTypes/SwapLabels/SwapLabelsWizard";
import { ConfigurableDataItemIds } from "../../misc/api/ApiConfigurableDataItems";
import { CorrectionTypesEnum } from "../../misc/api/apiTypes";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { faEnvelope, faList, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export interface DataCorrectionRequestProps {
    dcfRequestState: DcfRequestState;
    dispatch: React.Dispatch<DcfRequestAction>;
}

export interface DataCorrectionRequestFormRecordsFields {
    SubjectId: string;
    VisitId: string;
    SessionId: string;
    SessionTypeId: string;
    ChangeBackground: string;
    CorrectionTypeId: string;
}

export interface DataCorrectionRequestFormFields {
    SiteId: string;
    PIName: string;
    CorrectionRecords: { [key: string]: DataCorrectionRequestFormRecordsFields; };
}

const DataCorrectionRequestReducer = (state: DcfRequestState, action: DcfRequestAction): DcfRequestState => {
    switch (action.type) {
        case DcfRequestActionType.AddRecord:
            const recordId = new Date().getTime().toString();

            return {
                ...state,
                records: [...state.records, recordId],
                visitOptionsQueries: state.visitOptionsQueries.set(recordId, {
                    query: null,
                    isLoading: false
                }),
                sessionOptionsQueries: state.sessionOptionsQueries.set(recordId, {
                    query: null,
                    isLoading: false
                }),
                corrections: state.corrections.set(recordId, []),
            };
        case DcfRequestActionType.RemoveRecord:
            return {
                ...state,
                records: state.records.filter(x => x != action.id),
                selectedSubjects: state.selectedSubjects.remove(action.id),
                selectedVisits: state.selectedVisits.remove(action.id),
                selectedSessions: state.selectedSessions.remove(action.id),
                corrections: state.corrections.remove(action.id),
                miscCorrection: state.miscCorrection.remove(action.id),
                visitOptionsQueries: state.visitOptionsQueries.remove(action.id),
                sessionOptionsQueries: state.sessionOptionsQueries.remove(action.id)
            };
        case DcfRequestActionType.SelectSubjectOption:
            const subject = state.subjectOptionsQuery.value.find(x => x.id === action.subjectId);

            return {
                ...state,
                selectedSubjects: state.selectedSubjects.set(action.recordId, subject),
            };
        case DcfRequestActionType.SetVisibleWizard:
            return {
                ...state,
                visibleWizard: {
                    recordId: action.recordId,
                    wizard: action.wizard,
                    fieldValue: action.fieldValue
                },
            };
        case DcfRequestActionType.AddCorrection:
            const currentCorrections = state.corrections.get(state.visibleWizard.recordId);

            let newCorrections = [...currentCorrections];
            if (currentCorrections.filter(x => x.field === action.correction.field).length == 0) {
                newCorrections = [...newCorrections, action.correction];
            } else {

                newCorrections = currentCorrections.map(c => {
                    if (c.field !== action.correction.field) {
                        return c;
                    }

                    return {
                        ...c,
                        ...action.correction,
                    };
                });
            }

            return {
                ...state,
                corrections: state.corrections.set(state.visibleWizard.recordId, newCorrections),
            };
        case DcfRequestActionType.AddMiscCorrection:
            return {
                ...state,
                miscCorrection: state.miscCorrection.set(state.visibleWizard.recordId, action.correction),
            };
        case DcfRequestActionType.LoadingSubjectOptions:
            return {
                ...state,
                isLoadingSubjectOptions: true
            };
        case DcfRequestActionType.LoadSubjectsOptions:
            return {
                ...state,
                subjectOptionsQuery: action.query,
                isLoadingSubjectOptions: false,
                isLoadingVisits: false,
                isLoadingSessions: false,
                sessionOptionsQueries: initialDcfRequestState.sessionOptionsQueries
            };
        case DcfRequestActionType.LoadingVisitOptions:
            let sessionRecord = state.visitOptionsQueries.get(action.recordId);

            if (sessionRecord) {
                sessionRecord.isLoading = true;
            }

            return {
                ...state,
                visitOptionsQueries: sessionRecord
                    ? state.visitOptionsQueries.set(action.recordId, sessionRecord)
                    : state.visitOptionsQueries,
                isLoadingVisits: true
            };
        case DcfRequestActionType.LoadVisitsOptions:
            return {
                ...state,
                sessionOptionsQueries: state.sessionOptionsQueries.set(action.recordId, {
                    query: {
                        value: [],
                    },
                    isLoading: false
                }),
                visitOptionsQueries: state.visitOptionsQueries.set(action.recordId, {
                    query: action.visits,
                    isLoading: false
                }),
                isLoadingVisits: false,
                isLoadingSessions: false,
            };
        case DcfRequestActionType.SelectVisitOption:
            return {
                ...state,
                selectedVisits: state.selectedVisits.set(action.recordId, action.visit),
                selectedSessions: state.selectedSessions.set(action.recordId, undefined),
            };
        case DcfRequestActionType.LoadingSessionOptions:
            let visitRecord = state.sessionOptionsQueries.get(action.recordId);

            if (visitRecord) {
                visitRecord.isLoading = true;
            }

            return {
                ...state,
                sessionOptionsQueries: visitRecord
                    ? state.sessionOptionsQueries.set(action.recordId, visitRecord)
                    : state.sessionOptionsQueries,
                isLoadingSessions: true
            };
        case DcfRequestActionType.LoadSessionOptions:
            return {
                ...state,
                sessionOptionsQueries: state.sessionOptionsQueries.set(action.recordId, {
                    query: action.query,
                    isLoading: false
                }),
                isLoadingSessions: false
            };
        case DcfRequestActionType.SelectSessionOption:
            return {
                ...state,
                selectedSessions: state.selectedSessions.set(action.recordId, action.session),
            };
        case DcfRequestActionType.SelectSiteOption:
            return {
                ...state,
                siteId: action.siteId,
                selectedSubjects: state.selectedSubjects.map(() => undefined).toMap(),
                selectedVisits: state.selectedVisits.map(() => undefined).toMap(),
                selectedSessions: state.selectedSessions.map(() => undefined).toMap(),
            };
        case DcfRequestActionType.FailedToSave:
            return {
                ...state,
                errorMessage: action.message,
                isSubmitting: false,
            };
        case DcfRequestActionType.SaveSuccess:
            return {
                ...initialDcfRequestState,
                errorMessage: null,
                failedSavingAttachment: false,
                savedRequests: action.requestIds
            };
        case DcfRequestActionType.Submitting:
            return {
                ...state,
                isSubmitting: action.isSubmitting,
                errorMessage: null,
            };
        case DcfRequestActionType.ResetError:
            return {
                ...state,
                errorMessage: null,
            };
        case DcfRequestActionType.SetCorrectionType:
            return {
                ...state,
                corrections: state.corrections.set(action.recordId, state.corrections.get(action.recordId).map(c => ({ ...c, correctionTypeId: action.correctionType })))
            };
        case DcfRequestActionType.ResetForm:
            return initialDcfRequestState;
        default:
            throw new Error(`Unknown action: ${action.type}`);

    }
};

const emptyFileAttachments: Immutable.Map<string, any[]> = Immutable.Map([
    [
        defaultRecordId,
        [],
    ]
]);

export const DataCorrectionRequest = (props: DataCorrectionRequestProps) => {
    const { dcfRequestState, dispatch } = props;
    const { study, dataItemConfigurationCollection } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions, currentUser } = useSecurityContext();
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [fileAttachments, setFileAttachments] = React.useState<Immutable.Map<string, any[]>>(emptyFileAttachments);

    const formApi = useFormApi<DataCorrectionRequestFormFields>();
    const formState = useFormState<DataCorrectionRequestFormFields>();

    const dispatchInitialActions = async () => {
        const sitesResponse = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
        setSiteOptions(SiteOptionsSelector(currentUserPermissions, sitesResponse.value));
    }

    const onReset = () => {
        dispatchInitialActions();
    }

    React.useEffect(() => {
        dispatchInitialActions();
    }, []);

    React.useEffect(() => {
        const getSubjects = async () => {
            if (formState.values.SiteId) {
                dispatch(LoadingSubjectOptionsAction.create());
                const subjectsResponse = await client.client.getSubjects("id,number,status,createdWithSpirotracV,site", `siteId eq ${formState.values.SiteId}`, "number", "site");
                dispatch(LoadSubjectsOptionsAction.create(subjectsResponse));
            } else {
                dispatch(LoadSubjectsOptionsAction.create({ value: [] }));
                for (let recordId of props.dcfRequestState.records) {
                    formApi.setValue(`CorrectionRecords[${recordId}].SubjectId`, "");
                    props.dispatch(SelectSubjectOptionAction.create(recordId, undefined));
                    props.dispatch(LoadVisitOptionsAction.create(recordId, { value: [] }));
                    props.dispatch(LoadSessionOptionsAction.create(recordId, { value: [] }));
                }
            }
        }

        getSubjects();
    }, [formState.values.SiteId]);

    React.useEffect(() => {
        const getPrincipalInvestigator = async () => {
            if (formState.values.SiteId) {
                const siteResponse = await client.client.getSite(formState.values.SiteId, "contact");

                formApi.setValue("PrincipalInvestigatorName", siteResponse?.contact);
            }
        }

        getPrincipalInvestigator();
    }, [formState.values.SiteId]);

    const mapCorrections = (corrections: Correction[]) => {
        if (!corrections) {
            return [];
        }

        return corrections.map(c => ({ field: c.field, entity: c.entity, originalValue: c.oldValue, correctedValue: c.newValue }));
    }

    const onSubmit = async () => {
        dispatch(SubmittingAction.create(true));
        formApi.validate();

        const form = formApi.getState();

        if (form.invalid) {
            dispatch(SubmittingAction.create(false));
            return dispatch(FailedToSaveAction.create("Errors exist on the page"));
        }

        const requests: Array<DcfRequest> = [];
        const records = Object.values(form.values.CorrectionRecords).filter(x => x !== null);

        // TODO: Clean this up, we should be using either record keys or indexes but not a combination of both
        for (let recordIndex = 0; recordIndex < records.length; recordIndex++) {
            const record = records[recordIndex];
            const recordKey = dcfRequestState.records[recordIndex];

            const recordCorrections = dcfRequestState.corrections.get(recordKey);
            const recordMiscCorrections = dcfRequestState.miscCorrection.get(recordKey);

            if ((!recordCorrections || recordCorrections.length === 0) && (recordMiscCorrections === null || recordMiscCorrections === undefined)) {
                return dispatch(FailedToSaveAction.create("At least one correction is required within the request"));
            }

            try {
                const request = await client.client.postDcfRequest({
                    studyId: study.id,
                    siteId: form.values.SiteId,
                    createdByUser: currentUser.Username,
                    subjectId: record.SubjectId === "All" ? null : record.SubjectId,
                    visitId: record.VisitId === "All" ? null : record.VisitId,
                    sessionId: record.SessionId === "All" ? null : record.SessionId,
                    additionalComments: record.ChangeBackground,
                    originalEntry: recordMiscCorrections ? recordMiscCorrections.fromDetails : null,
                    newEntry: recordMiscCorrections ? recordMiscCorrections.toDetails : null,
                    corrections: mapCorrections(recordCorrections),
                    correctionTypeId: recordCorrections.length > 0 ? recordCorrections[0].correctionTypeId : CorrectionTypesEnum.Miscellaneous,
                });

                for (const file of fileAttachments.get(recordKey) ?? []) {
                    const fileResult = await legacyClient.postFile(study.id, AttachmentRelatesTo.DcfRequest, request.id, file);

                    if (fileResult.Errors) {
                        return dispatch(FailedToSaveAction.create(fileResult.Errors.map(e => e.Message).join(",")));
                    }
                }

                requests.push(request);
            } catch (ex) {
                if (ex.message) {
                    return dispatch(FailedToSaveAction.create(ex.message));
                }

                return dispatch(FailedToSaveAction.create("Failed to save the request to the server"));
            }
        }

        dispatch(SaveSuccessAction.create(requests));
        dispatch(SubmittingAction.create(false));
        formApi.reset();

        if (document && document.documentElement) {
            document.documentElement.scrollTop = 0;
        }
    }

    return (
        <>
            <Alert
                show={dcfRequestState.errorMessage !== null}
                category={AlertCategoryEnum.Danger}
            >
                {dcfRequestState.errorMessage}
            </Alert>
            <Alert
                show={false} //props.failedSavingAttachments was unset, making this alert useless
                category={AlertCategoryEnum.Danger}
            >
                Failed to save an attachment linked to the current DCF request, cancelled
                further DCF request submission
            </Alert>
            <Alert
                show={dcfRequestState.savedRequests.length > 0}
                category={AlertCategoryEnum.Success}
            >
                Data correction request received, reference number(s):{" "}
                {dcfRequestState.savedRequests.map(r => (
                    <React.Fragment key={`DcfRequestLink-${r.id}`}>
                        <Link to={`/Dcf/View/${r.id}`}>
                            {r.label}
                        </Link>{" "}
                    </React.Fragment>
                ))}
            </Alert>
            <DataCorrectionRequestForm {...props}
                siteOptions={siteOptions}
                selectedSubjects={dcfRequestState.selectedSubjects}
                fileAttachments={fileAttachments}
                records={dcfRequestState.records}
                corrections={dcfRequestState.corrections}
                miscCorrections={dcfRequestState.miscCorrection}
                onReset={onReset}
                setFileAttachments={setFileAttachments}
                dispatch={dispatch}
                dcfRequestState={dcfRequestState}
                onSubmit={() => onSubmit()}
            />
        </>
    );
}

export interface DataCorrectionRequestFormProps {
    records: string[];
    siteOptions: Site[];
    selectedSubjects: Immutable.Map<string, Subject>;
    corrections: Immutable.Map<string, Array<Correction>>;
    miscCorrections: Immutable.Map<string, MiscCorrection>;
    fileAttachments: Immutable.Map<string, any[]>;
    onReset: () => void;
    setFileAttachments: any;
    dispatch: React.Dispatch<DcfRequestAction>;
    dcfRequestState: DcfRequestState;
    onSubmit: () => void;
}

export const DataCorrectionRequestForm = (props: DataCorrectionRequestFormProps) => {
    const formApi = useFormApi();
    const formState = useFormState();

    const onSubmit = () => {
        props.dispatch(ResetErrorAction.create());
        formApi.validate();

        const form = formApi.getState();

        if (form.invalid) {
            return props.dispatch(FailedToSaveAction.create("Errors exist on the page"));
        }

        if (!hasCorrections()) {
            return props.dispatch(FailedToSaveAction.create("At least one data correction is required"));
        }

        props.onSubmit();
    };

    const onReset = formApi => {
        props.setFileAttachments(emptyFileAttachments);
        props.dispatch(ResetFormAction.create());
        formApi.reset();
        //After reset, firing the initial requests for sites etc as the component has been reset.
        props.onReset();
    };

    const queryDataSelector = (
        queryMap: Immutable.Map<string, DcfRecordState<any>>,
        recordId: string
    ): DcfRecordState<any> => {
        if (queryMap.get(recordId) === undefined) {
            return null;
        }

        return queryMap.get(recordId);
    };

    const hasAnyFileAttachments = () => {
        return props.fileAttachments.some(fa => fa.length > 0);
    }

    const hasCorrections = () => {
        return (
            !!props.corrections.find(corrections => !!corrections && corrections.length > 0) ||
            !!props.miscCorrections.find(mc => !!mc)
        );
    }

    const renderRecords = (formApi: any) => {
        return (
            <>
                {props.records.map((r, index) => {
                    const sessions: DcfRecordState<QueryOfSession> = queryDataSelector(
                        props.dcfRequestState.sessionOptionsQueries,
                        r
                    );
                    const visits: DcfRecordState<QueryOfVisit> = queryDataSelector(
                        props.dcfRequestState.visitOptionsQueries,
                        r
                    );

                    return (
                        <DataCorrectionRequestFormRecord
                            key={`Record${r}`}
                            setRecordFileAttachmentUploader={() => { }}
                            recordId={r}
                            recordIndex={index}
                            subjectOptions={props.dcfRequestState.subjectOptionsQuery?.value ?? []}
                            visits={visits}
                            sessions={sessions}
                            selectedSubject={props.selectedSubjects.get(r)}
                            selectedVisit={props.dcfRequestState.selectedVisits.get(r)}
                            selectedSession={props.dcfRequestState.selectedSessions.get(r)}
                            isRemoveRecordButtonDisabled={props.records.length === 1}
                            isLoadingSubjectOptions={props.dcfRequestState.isLoadingSubjectOptions}
                            corrections={props.corrections.get(r)}
                            miscCorrection={props.miscCorrections.get(r)}
                            dispatch={props.dispatch}
                            dcfRequestState={props.dcfRequestState}
                            onRemoveClick={id => props.dispatch(RemoveRecordAction.create(id))}
                            onFileAttachmentChanged={(fa) => {
                                props.setFileAttachments(props.fileAttachments.set(r, fa));
                            }}
                            onSubjectChange={s =>
                                props.dispatch(
                                    SelectSubjectOptionAction.create(r, s)
                                )
                            }
                            onVisitChange={v =>
                                props.dispatch(
                                    SelectVisitOptionAction.create(
                                        r,
                                        visits.query.value.find(x => x.id == v),
                                        index
                                    )
                                )
                            }
                            onSessionChange={s =>
                                props.dispatch(
                                    SelectSessionOptionAction.create(
                                        r,
                                        sessions.query.value.find(x => x.id == s),
                                        index,
                                        formApi
                                    )
                                )
                            }
                        />
                    );
                })}
            </>
        );
    };

    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">Request a Data Correction</h2>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <Alert show={true} category={AlertCategoryEnum.Info}>
                        Please complete this form to inform Vitalograph of
                        errors made at site regarding data which needs to be
                        corrected.
                    </Alert>

                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div className="form-group col-md-6">
                                <label htmlFor="select-site">Site</label>
                                <Dropdown
                                    id="select-site"
                                    disabled={hasCorrections()}
                                    field="SiteId"
                                    className="form-control"
                                    onChange={e => {
                                        props.dispatch(
                                            SelectSiteOptionAction.create(
                                                e.target.value,
                                                formApi
                                            )
                                        );
                                    }}
                                >
                                    <Option
                                        key={"OptionSiteBlank"}
                                        value={[]}
                                    />
                                    {props.siteOptions.map(so => (
                                        <Option
                                            key={`Option${so.id}`}
                                            value={so.id}
                                        >
                                            {so.number}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                            <div className="form-group col-md-6">
                                <label htmlFor="piName">
                                    Principal Investigator Name
                                </label>
                                <TextBox
                                    id="input-principalInvestigatorName"
                                    field="PrincipalInvestigatorName"
                                    readOnly
                                    disabled
                                />
                            </div>
                        </div>
                    </div>

                    {renderRecords(formApi)}
                </div>
            </div>

            <div className="row">
                <div className="col-md-6">
                    <button
                        className="btn btn-primary btn-outline"
                        type="button"
                        disabled={
                            !formState.dirty && !hasAnyFileAttachments()
                        }
                        onClick={() => onReset(formApi)}
                    >
                        <FontAwesomeIcon icon={faRotateLeft} /> Reset Form
                    </button>
                </div>
                <div className="col-md-6 text-right">
                    <button
                        className="btn btn-primary btn-outline"
                        type="button"
                        disabled={props.dcfRequestState.isSubmitting}
                        onClick={() =>
                            props.dispatch(AddRecordAction.create())
                        }
                    >
                        <FontAwesomeIcon icon={faList} /> Add Additional Data
                        Correction
                    </button>
                    &nbsp;
                    <button
                        className="btn btn-primary"
                        type="submit"
                        onClick={() => onSubmit()}
                        disabled={props.dcfRequestState.isSubmitting}
                    >
                        <FontAwesomeIcon icon={faEnvelope} /> Submit
                    </button>
                </div>
            </div>
            <br />
        </>
    );
}

export const DataCorrectionRequestWrapper = () => {
    const [dcfRequestState, dispatch] = React.useReducer(DataCorrectionRequestReducer, initialDcfRequestState);
    const { dataItemConfigurationCollection, study } = useCurrentStudyConfigurationContext();

    const dispatchAddCorrections = (corrections: Correction[]) => {
        for (let i = 0; i < corrections.length; i++) {
            dispatch(AddCorrectionAction.create(corrections[i]));
        }

        dispatch(
            SetVisibleWizardAction.create(null, null, null)
        );
    }

    const dispatchSetCorrectionType = (recordId: string, correctionType: CorrectionTypesEnum) => {
        dispatch(SetCorrectionTypeAction.create(recordId, correctionType));
    }

    const subjectConfig = () => dataItemConfigurationCollection.find(dic => dic.configurableDataItemId == ConfigurableDataItemIds.SubjectId);

    return (
        <DocumentTitle title="Request a Data Correction | Vitalograph Web Portal">
            <Form id="requestDcfForm">
                <DataCorrectionRequest
                    dcfRequestState={dcfRequestState}
                    dispatch={dispatch}
                />
            </Form>
            <ErrorBoundary>
                {dcfRequestState.visibleWizard.wizard === VisibleWizards.Misc ? (<MiscCorrectionWizard
                    correction={dcfRequestState.miscCorrection.get(dcfRequestState.visibleWizard.recordId)}
                    isVisible={dcfRequestState.visibleWizard.wizard === VisibleWizards.Misc}
                    onClose={() =>
                        dispatch(SetVisibleWizardAction.create(null, null, null))
                    }
                    onComplete={correction => {
                        dispatch(AddMiscCorrectionAction.create(correction));
                        dispatch(SetCorrectionTypeAction.create(dcfRequestState.visibleWizard.recordId, CorrectionTypesEnum.Miscellaneous));
                        dispatch(SetVisibleWizardAction.create(null, null, null));
                    }}
                />) : null}
            </ErrorBoundary>
            <ErrorBoundary>
                {dcfRequestState.visibleWizard.wizard === VisibleWizards.CoughSessionSuppress ? <SuppressCoughSessionWizard
                    correctedSuppressState={
                        dcfRequestState.corrections.get(dcfRequestState.visibleWizard.recordId) &&
                        dcfRequestState.corrections.get(dcfRequestState.visibleWizard.recordId)[0] &&
                        dcfRequestState.corrections.get(dcfRequestState.visibleWizard.recordId)[0].newValue
                    }
                    originalSuppressState={dcfRequestState.visibleWizard.fieldValue}
                    isVisible={dcfRequestState.visibleWizard.wizard === VisibleWizards.CoughSessionSuppress}
                    onClose={
                        () =>
                            dispatch(SetVisibleWizardAction.create(null, null, null))
                    }
                    onComplete={(corrections) => {
                        dispatchAddCorrections(corrections);
                        dispatchSetCorrectionType(dcfRequestState.visibleWizard.recordId, CorrectionTypesEnum.CoughSessionSuppress);
                    }} /> : null}
            </ErrorBoundary>
            <ErrorBoundary>

                {dcfRequestState.visibleWizard.wizard === VisibleWizards.RecordingStart ? (<RecordingStartCorrectionWizard
                    isVisible={
                        dcfRequestState.visibleWizard.wizard === VisibleWizards.RecordingStart
                    }
                    sessionCurrentDateTime={dcfRequestState.visibleWizard.fieldValue}
                    onClose={() =>
                        dispatch(SetVisibleWizardAction.create(null, null, null))
                    }
                    onComplete={(corrections) => {
                        dispatchAddCorrections(corrections);
                        dispatchSetCorrectionType(dcfRequestState.visibleWizard.recordId, CorrectionTypesEnum.RecordingStart);
                    }}
                />) : null}

            </ErrorBoundary>
            <ErrorBoundary>
                {dcfRequestState.visibleWizard.wizard === VisibleWizards.SubjectNumber ? (<SubjectIdCorrectionWizard
                    subjectIdFieldDisplayText={subjectConfig().displayText}
                    isVisible={
                        dcfRequestState.visibleWizard.wizard === VisibleWizards.SubjectNumber
                    }
                    siteNumber={dcfRequestState.selectedSubjects.get(dcfRequestState.visibleWizard.recordId).site.number}
                    originalSubjectId={dcfRequestState.visibleWizard.fieldValue}
                    correctedSubjectId={
                        dcfRequestState.corrections.get(dcfRequestState.visibleWizard.recordId) &&
                        dcfRequestState.corrections.get(dcfRequestState.visibleWizard.recordId)[0] &&
                        dcfRequestState.corrections.get(dcfRequestState.visibleWizard.recordId)[0].newValue}
                    onClose={() =>
                        dispatch(SetVisibleWizardAction.create(null, null, null))
                    }
                    onComplete={(corrections) => {
                        dispatchAddCorrections(corrections);
                        dispatchSetCorrectionType(dcfRequestState.visibleWizard.recordId, CorrectionTypesEnum.SubjectNumber);
                    }}
                />) : null}

            </ErrorBoundary>
            <ErrorBoundary>
                {dcfRequestState.visibleWizard.wizard === VisibleWizards.SubjectType ? (
                    <SubjectTypeCorrectionWizard
                        isVisible={
                            dcfRequestState.visibleWizard.wizard === VisibleWizards.SubjectType
                        }
                        originalSubjectType={dcfRequestState.visibleWizard.fieldValue}
                        correctedSubjectType={
                            dcfRequestState.corrections.get(
                                dcfRequestState.visibleWizard.recordId
                            ) &&
                            dcfRequestState.corrections.get(
                                dcfRequestState.visibleWizard.recordId
                            )[0] &&
                            dcfRequestState.corrections.get(
                                dcfRequestState.visibleWizard.recordId
                            )[0].newValue
                        }
                        onClose={() =>
                            dispatch(
                                SetVisibleWizardAction.create(null, null, null)
                            )
                        }
                        onComplete={(corrections) => {
                            dispatchAddCorrections(corrections);
                            dispatchSetCorrectionType(dcfRequestState.visibleWizard.recordId, CorrectionTypesEnum.SubjectType);
                        }}
                    />
                ) : null}
            </ErrorBoundary>
            <ErrorBoundary>
                {dcfRequestState.visibleWizard.wizard === VisibleWizards.EntityLabels ? <EntityLabelCorrectionWizard
                    studyId={study.id}
                    siteId={dcfRequestState.siteId}
                    isVisible={dcfRequestState.visibleWizard.wizard === VisibleWizards.EntityLabels}
                    subjectIdFieldDisplayText={subjectConfig().displayText}
                    originalValue={dcfRequestState.visibleWizard.fieldValue}
                    onClose={() =>
                        dispatch(SetVisibleWizardAction.create(null, null, null))
                    }
                    onComplete={(corrections) => {
                        dispatchAddCorrections(corrections);
                        dispatchSetCorrectionType(dcfRequestState.visibleWizard.recordId, CorrectionTypesEnum.MoveSession);
                    }}
                /> : null}
            </ErrorBoundary>
            <ErrorBoundary>
                {dcfRequestState.visibleWizard.wizard === VisibleWizards.MediaSerial ? (<MediaSerialCorrectionWizard
                    isVisible={
                        dcfRequestState.visibleWizard.wizard === VisibleWizards.MediaSerial
                    }
                    originalMediaSerial={dcfRequestState.visibleWizard.fieldValue}
                    onClose={() =>
                        dispatch(SetVisibleWizardAction.create(null, null, null))
                    }
                    onComplete={(corrections) => {
                        dispatchAddCorrections(corrections);
                        dispatchSetCorrectionType(dcfRequestState.visibleWizard.recordId, CorrectionTypesEnum.MediaSerial);
                        dispatch(SetVisibleWizardAction.create(null, null, null));
                    }}
                />) : null}

            </ErrorBoundary>

            <ErrorBoundary>
                {dcfRequestState.visibleWizard.wizard === VisibleWizards.SwapLabels ? <Form
                    initialValues={
                        {
                            Subject: dcfRequestState.visibleWizard.fieldValue.subject.id,
                        }
                    }
                ><SwapLabelsWizard
                    isVisible={dcfRequestState.visibleWizard.wizard === VisibleWizards.SwapLabels}
                    subjectIdFieldDisplayText={subjectConfig().displayText}
                        originalValue={dcfRequestState.visibleWizard.fieldValue}
                        siteId={dcfRequestState.siteId}
                    onClose={() =>
                        dispatch(SetVisibleWizardAction.create(null, null, null))
                    }
                    onComplete={(corrections) => {
                        dispatchAddCorrections(corrections);
                        dispatchSetCorrectionType(dcfRequestState.visibleWizard.recordId, CorrectionTypesEnum.SwapLabels);
                    }}
                /></Form> : null}
            </ErrorBoundary>
        </DocumentTitle>
    );
}

export default DataCorrectionRequestWrapper;
